import { useCallback, useEffect, useMemo, useState, type MouseEvent } from 'react';
import styled from 'styled-components';
import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Button from '../../components/button/Button';
import DatePicker from '../../components/datepicker/DatePicker';
import Icon from '../../components/icon/Icon';
import Table from '../../components/table/table';
import TableSkeleton from '../../components/TableSkeleton/TableSkeleton';
import { useConfig } from '../../context/ConfigContext';
import {
  downloadHistoricalLiquidationPdf,
  downloadLiquidationPdf,
  getLiquidationHistory,
} from '../../services/GuaranteesService';
import { useToast } from '../../components/toast/toast';
import theme from '../../styles/theme';
import { resolveLocalCurrencyCode } from '../../utils/quotationAssets';

interface ProtectorDetailsLiquidationsSectionProps {
  protectorId?: string;
}

type LiquidationHistoryRow = Record<string, unknown>;

const HistoryDownloadAction = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

function parseIsoDateLocal(ymd: string): Date | null {
  if (!ymd?.trim()) return null;
  const m = ymd.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
  return new Date(y, mo - 1, d);
}

function startOfTodayLocal(): Date {
  const t = new Date();
  return new Date(t.getFullYear(), t.getMonth(), t.getDate());
}

function pickRow(row: LiquidationHistoryRow, ...keys: string[]): unknown {
  for (const k of keys) {
    const v = row[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
}

function toFiniteAmount(v: unknown): number | null {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') {
    const trimmed = v.trim();
    const nJs = Number(trimmed);
    if (Number.isFinite(nJs)) return nJs;
    return null;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function getHistorySortTime(row: LiquidationHistoryRow): number {
  const raw = pickRow(row, 'generatedAt', 'generated_at', 'createdAt', 'created_at');
  if (raw == null) return 0;
  const t = new Date(String(raw)).getTime();
  return Number.isFinite(t) ? t : 0;
}

function normalizeHistoryPage(payload: Record<string, unknown>) {
  const contentRaw = payload.content;
  const content = Array.isArray(contentRaw) ? (contentRaw as LiquidationHistoryRow[]) : [];
  const sorted = [...content].sort((a, b) => getHistorySortTime(b) - getHistorySortTime(a));

  const totalRaw =
    (payload.totalElements as unknown) ?? (payload.total_elements as unknown) ?? sorted.length;
  const totalElements = typeof totalRaw === 'number' ? totalRaw : Number(totalRaw) || 0;

  const pageRaw = payload.number ?? payload.page ?? 0;
  const pageNum = Number(pageRaw);
  const page = Number.isFinite(pageNum) && pageNum >= 0 ? pageNum : 0;

  const sizeRaw = payload.size ?? payload.pageSize ?? payload.page_size ?? 10;
  const sizeNum = Number(sizeRaw);
  const size = Number.isFinite(sizeNum) && sizeNum > 0 ? sizeNum : 10;

  return {
    content: sorted,
    page,
    size,
    totalElements,
  };
}

const ProtectorDetailsLiquidationsSection = ({
  protectorId,
}: ProtectorDetailsLiquidationsSectionProps) => {
  const config = useConfig();
  const maxYears = config.liquidationsMaxRangeYears ?? 20;
  const localCurrencyCode = resolveLocalCurrencyCode(config);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [historyRows, setHistoryRows] = useState<LiquidationHistoryRow[]>([]);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 10,
    totalElements: 0,
  });
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadingHistoryId, setDownloadingHistoryId] = useState<number | null>(null);

  const maxDatePick = useMemo(() => new Date(), []);

  const { fromErr, toErr } = useMemo(() => {
    let fe = '';
    let te = '';
    const fromD = parseIsoDateLocal(fromDate);
    const toD = parseIsoDateLocal(toDate);
    const today = startOfTodayLocal();

    if (fromDate && !fromD) fe = 'Fecha desde inválida';
    if (toDate && !toD) te = 'Fecha hasta inválida';

    if (fromD && fromD.getTime() > today.getTime()) {
      fe = 'Las fechas no pueden ser futuras';
    }
    if (toD && toD.getTime() > today.getTime()) {
      te = 'Las fechas no pueden ser futuras';
    }
    if (fromD && toD && fromD.getTime() > toD.getTime()) {
      fe = 'Fecha desde no puede ser posterior a Fecha hasta';
    }
    if (fromD && toD && fe === '' && te === '') {
      const limit = new Date(fromD);
      limit.setFullYear(limit.getFullYear() + maxYears);
      if (toD.getTime() > limit.getTime()) {
        te = `El rango no puede superar los ${maxYears} años`;
      }
    }

    return { fromErr: fe, toErr: te };
  }, [fromDate, toDate, maxYears]);

  const canSubmit =
    Boolean(fromDate && toDate && parseIsoDateLocal(fromDate) && parseIsoDateLocal(toDate)) &&
    !fromErr &&
    !toErr &&
    !downloadLoading;

  const fetchHistory = useCallback(async () => {
    if (!protectorId) return;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const response = await getLiquidationHistory(protectorId, pagination.page, pagination.size);
      if (response.success && response.data && typeof response.data === 'object') {
        const norm = normalizeHistoryPage(response.data as Record<string, unknown>);
        setHistoryRows(norm.content);
        setPagination((prev) => ({
          ...prev,
          page: norm.page,
          size: norm.size,
          totalElements: norm.totalElements,
        }));
      } else {
        setHistoryError('Error al cargar el historial de liquidaciones');
      }
    } catch (err) {
      console.error(err);
      setHistoryError('Error al cargar el historial de liquidaciones');
    } finally {
      setHistoryLoading(false);
    }
  }, [protectorId, pagination.page, pagination.size]);

  useEffect(() => {
    if (!protectorId) return;
    void fetchHistory();
  }, [protectorId, fetchHistory]);

  const handleChangeHistoryPage = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleDownloadPdf = async () => {
    if (!protectorId || !canSubmit) return;
    setDownloadLoading(true);
    try {
      await downloadLiquidationPdf(protectorId, fromDate, toDate);
      await fetchHistory();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'No se pudo descargar el PDF. Intentá nuevamente.';
      useToast.error({ title: message });
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleDownloadHistorical = async (historyId: number) => {
    if (!protectorId || !historyId) return;
    setDownloadingHistoryId(historyId);
    try {
      const response = await downloadHistoricalLiquidationPdf(protectorId, historyId);
      if (response?.success && 'data' in response && response.data) {
        const blob = new Blob([response.data as ArrayBuffer], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      } else {
        useToast.error({ title: 'No se pudo descargar el PDF. Intentá nuevamente.' });
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'No se pudo descargar el PDF. Intentá nuevamente.';
      useToast.error({ title: message });
    } finally {
      setDownloadingHistoryId(null);
    }
  };

  const formatGeneratedAt = (row: LiquidationHistoryRow): string => {
    const raw = pickRow(row, 'generatedAt', 'generated_at', 'createdAt', 'created_at');
    if (!raw) return '-';
    const d = new Date(String(raw));
    if (Number.isNaN(d.getTime())) return String(raw);
    return d.toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatPeriod = (row: LiquidationHistoryRow): string => {
    const from = pickRow(row, 'rangeFrom', 'range_from', 'fromDate', 'from_date');
    const to = pickRow(row, 'rangeTo', 'range_to', 'toDate', 'to_date');
    if (from == null && to == null) return '-';
    const a = from != null ? String(from).slice(0, 10) : '-';
    const b = to != null ? String(to).slice(0, 10) : '-';
    return `${a} — ${b}`;
  };

  const formatQuotation = (row: LiquidationHistoryRow): string => {
    const v = pickRow(
      row,
      'applied_quotation',
      'appliedQuotation',
      'usdQuotationApplied',
      'usd_quotation_applied',
      'quotationUsdArs',
      'quotation_usd_ars',
      'usdQuotationSale',
      'usd_quotation_sale',
      'appliedUsdSale',
      'applied_usd_sale'
    );
    const n = toFiniteAmount(v);
    if (n == null) return '-';
    const hasFraction = Math.abs(n % 1) > 1e-9;
    const formatted = Math.abs(n).toLocaleString('es-AR', {
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: hasFraction ? 4 : 0,
    });
    return `1 USD = $ ${formatted}`;
  };

  const formatMoneyArs = (row: LiquidationHistoryRow): string => {
    const v = pickRow(
      row,
      'saldo_final_ars',
      'saldoFinalArs',
      'closingBalanceArs',
      'closing_balance_ars',
      'finalBalanceArs',
      'final_balance_ars'
    );
    const n = toFiniteAmount(v);
    if (n == null) return '-';
    return `$ ${Math.round(n).toLocaleString('es-AR')}`;
  };

  const formatMoneyUsd = (row: LiquidationHistoryRow): string => {
    const v = pickRow(
      row,
      'saldo_final_usd',
      'saldoFinalUsd',
      'closingBalanceUsd',
      'closing_balance_usd',
      'finalBalanceUsd',
      'final_balance_usd'
    );
    const n = toFiniteAmount(v);
    if (n == null) return '-';
    return `USD ${n.toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatGeneratedBy = (row: LiquidationHistoryRow): string => {
    const display = pickRow(
      row,
      'generated_by_display_name',
      'generatedByDisplayName',
      'generatedByName',
      'generated_by_name',
      'generatedBy',
      'generated_by'
    );
    if (display != null && String(display).trim()) return String(display);
    const id = pickRow(row, 'generatedByUserId', 'generated_by_user_id');
    if (id != null && id !== '') return `Usuario ${id}`;
    return '-';
  };

  const columns = [
    {
      name: (
        <P variant="secondary-2" type="3" weight="bold" withMargin={false}>
          Fecha generación
        </P>
      ),
      selector: (row: LiquidationHistoryRow) => (
        <P variant="secondary-2" type="3" weight="regular" withMargin={false}>
          {formatGeneratedAt(row)}
        </P>
      ),
      minWidth: '150px',
      grow: 1,
    },
    {
      name: (
        <P variant="secondary-2" type="3" weight="bold" withMargin={false}>
          Generado por
        </P>
      ),
      selector: (row: LiquidationHistoryRow) => (
        <P variant="secondary-2" type="3" weight="regular" withMargin={false}>
          {formatGeneratedBy(row)}
        </P>
      ),
      minWidth: '120px',
      grow: 1,
    },
    {
      name: (
        <P variant="secondary-2" type="3" weight="bold" withMargin={false}>
          Periodo
        </P>
      ),
      selector: (row: LiquidationHistoryRow) => (
        <P variant="secondary-2" type="3" weight="regular" withMargin={false}>
          {formatPeriod(row)}
        </P>
      ),
      minWidth: '170px',
      grow: 1,
    },
    {
      name: (
        <P variant="secondary-2" type="3" weight="bold" withMargin={false}>
          Cotización aplicada
        </P>
      ),
      selector: (row: LiquidationHistoryRow) => (
        <P variant="secondary-2" type="3" weight="regular" withMargin={false}>
          {formatQuotation(row)}
        </P>
      ),
      minWidth: '140px',
      grow: 1,
    },
    {
      name: (
        <P variant="secondary-2" type="3" weight="bold" withMargin={false}>
          {`Saldo Final ${localCurrencyCode}`}
        </P>
      ),
      selector: (row: LiquidationHistoryRow) => (
        <P variant="secondary-2" type="3" weight="regular" withMargin={false}>
          {formatMoneyArs(row)}
        </P>
      ),
      minWidth: '120px',
      grow: 1,
    },
    {
      name: (
        <P variant="secondary-2" type="3" weight="bold" withMargin={false}>
          Saldo Final USD
        </P>
      ),
      selector: (row: LiquidationHistoryRow) => (
        <P variant="secondary-2" type="3" weight="regular" withMargin={false}>
          {formatMoneyUsd(row)}
        </P>
      ),
      minWidth: '120px',
      grow: 1,
    },
    {
      name: (
        <P variant="secondary-2" type="3" weight="bold" withMargin={false}>
          Acción
        </P>
      ),
      selector: (row: LiquidationHistoryRow) => {
        const rawId = pickRow(row, 'id');
        const id = typeof rawId === 'number' ? rawId : Number(rawId);
        const loading = downloadingHistoryId === id;
        return (
          <HistoryDownloadAction
            type="button"
            disabled={!Number.isFinite(id) || loading}
            aria-label={`Descargar liquidación histórica ${id}`}
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              if (Number.isFinite(id)) void handleDownloadHistorical(id);
            }}
          >
            <Icon
              name="download_outline"
              variant="info"
              modifier="regular"
              width={20}
              height={20}
              cursor="pointer"
              disabled={!Number.isFinite(id) || loading}
            />
            <P variant="info" modifier="regular" type="3" weight="bold" withMargin={false}>
              Descargar
            </P>
          </HistoryDownloadAction>
        );
      },
      minWidth: '130px',
      grow: 0.8,
    },
  ];

  return (
    <>
      <Card
        display="flex"
        flexDirection="column"
        gap="8px"
        margin={{ top: '2', bottom: '4' }}
      >
        <H withMargin={false} type="2" weight="bold" modifier="strong">
          Generar liquidación
        </H>
        <P variant="secondary-2" type="3" modifier="regular" withMargin={false}>
          Seleccioná el rango de fechas para generar el PDF de liquidación del protector.
        </P>

        <Card
          display="flex"
          flexWrap="wrap"
          flexDirection="row"
          gap="16px"
          alignItems="flex-end"
          margin={{ top: '2' }}
        >
          <Card display="flex" flexDirection="column" gap="4px" style={{ minWidth: '200px' }}>
            <DatePicker
              placeholder="Fecha desde"
              id="liquidacion-fecha-desde"
              defaultValue=""
              disabled={false}
              maxDate={maxDatePick}
              onChange={(v) => setFromDate(typeof v === 'string' ? v : '')}
            />
            {fromErr ? (
              <P variant="neutral" modifier="weak" type="4" withMargin={false}>
                {fromErr}
              </P>
            ) : null}
          </Card>
          <Card display="flex" flexDirection="column" gap="4px" style={{ minWidth: '200px' }}>
            <DatePicker
              placeholder="Fecha hasta"
              id="liquidacion-fecha-hasta"
              defaultValue=""
              disabled={false}
              maxDate={maxDatePick}
              onChange={(v) => setToDate(typeof v === 'string' ? v : '')}
            />
            {toErr ? (
              <P variant="neutral" modifier="weak" type="4" withMargin={false}>
                {toErr}
              </P>
            ) : null}
          </Card>
          <Card display="flex" alignItems="flex-end" style={{ flexShrink: 0, minWidth: '240px' }}>
            <Button
              variant="primary"
              icon="download_outline"
              iconAlign="left"
              disabled={!canSubmit}
              onClick={() => void handleDownloadPdf()}
              title={
                canSubmit
                  ? undefined
                  : 'Seleccioná un rango de fechas válido para descargar el PDF'
              }
            >
              Descargar PDF
            </Button>
          </Card>
        </Card>

        <P variant="secondary-2" type="4" modifier="weak" withMargin={false}>
          El PDF generado incluye: datos del protector, integraciones del rango, movimientos de
          afrontes/honras y totales con saldo final. Cada descarga queda registrada en el historial.
        </P>
      </Card>

      <Card display="flex" justifyContent="space-between" alignItems="center" margin={{ bottom: '3' }}>
        <H withMargin={false} type="2" weight="bold" modifier="strong">
          Historial de liquidaciones
        </H>
      </Card>

      {historyLoading ? (
        <Card>
          <TableSkeleton rows={4} columns={7} />
        </Card>
      ) : historyError ? (
        <Card>
          <P variant="neutral" modifier="weak" withMargin={false}>
            {historyError}
          </P>
        </Card>
      ) : historyRows.length > 0 ? (
        <Card>
          <Table
            columns={columns}
            data={historyRows}
            tableData={pagination}
            handleChangePage={handleChangeHistoryPage}
          />
        </Card>
      ) : (
        <Card>
          <P variant="neutral" modifier="weak" withMargin={false}>
            Aun no se generaron liquidaciones para este protector.
          </P>
        </Card>
      )}
    </>
  );
};

export default ProtectorDetailsLiquidationsSection;
